import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Pencil, Plus, Tags, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    Promise.all([api.get("/categories"), api.get("/products")])
      .then(([c, p]) => {
        setCategories(c.data);
        setProducts(p.data);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const counts = products.reduce((acc, p) => {
    if (p.category_id) acc[p.category_id] = (acc[p.category_id] || 0) + 1;
    return acc;
  }, {});

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await api.post("/categories", { name: newName.trim() });
      toast.success("Category added");
      setNewName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const handleRename = async () => {
    if (!editName.trim() || !editing) return;
    try {
      await api.put(`/categories/${editing.id}`, { name: editName.trim() });
      toast.success("Category renamed");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/categories/${deleteTarget.id}`);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= categories.length) return;
    const next = [...categories];
    [next[index], next[target]] = [next[target], next[index]];
    setCategories(next);
    api.put("/categories/reorder", { ordered_ids: next.map((c) => c.id) }).catch((err) => {
      toast.error(formatApiError(err));
      load();
    });
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="categories-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Categories</h1>
        <p className="mt-1 text-sm text-zinc-500">Organize your products. Each category gets a shareable URL.</p>
      </div>

      <div className="flex gap-3">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder="New category name"
          data-testid="category-name-input"
          className="rounded-xl bg-white"
        />
        <Button onClick={handleAdd} disabled={adding || !newName.trim()} data-testid="add-category-button" className="shrink-0 rounded-full bg-black px-5 text-white hover:bg-zinc-800">
          <Plus className="mr-1.5 h-4 w-4" />
          Add
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-2xl border border-black/5 bg-white" />
          ))}
        </div>
      ) : categories.length === 0 ? (
        <div className="flex flex-col items-center rounded-3xl border border-dashed border-black/10 bg-white px-6 py-16 text-center" data-testid="categories-empty-state">
          <Tags className="h-8 w-8 text-zinc-300" />
          <p className="mt-4 font-heading text-lg font-medium">No categories yet</p>
          <p className="mt-1 text-sm text-zinc-500">Create your first category above.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="categories-list">
          {categories.map((c, i) => (
            <div key={c.id} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-4" data-testid={`category-row-${c.slug}`}>
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`category-up-${c.slug}`} className="rounded p-0.5 text-zinc-300 transition-colors hover:text-black disabled:opacity-30">
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === categories.length - 1} aria-label="Move down" data-testid={`category-down-${c.slug}`} className="rounded p-0.5 text-zinc-300 transition-colors hover:text-black disabled:opacity-30">
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{c.name}</p>
                <p className="mt-0.5 text-xs text-zinc-400">
                  /category/{c.slug} &middot; {counts[c.id] || 0} {counts[c.id] === 1 ? "product" : "products"}
                </p>
              </div>
              <button
                onClick={() => {
                  setEditing(c);
                  setEditName(c.name);
                }}
                aria-label="Rename category"
                data-testid={`edit-category-${c.slug}`}
                className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-black"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={() => setDeleteTarget(c)}
                aria-label="Delete category"
                data-testid={`delete-category-${c.slug}`}
                className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-3xl sm:max-w-sm" data-testid="edit-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Rename category</DialogTitle>
            <DialogDescription>This also updates its shareable URL.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="edit-cat-name">Name</Label>
            <Input id="edit-cat-name" value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-category-name-input" className="rounded-xl" />
          </div>
          <Button onClick={handleRename} data-testid="save-category-button" className="w-full rounded-full bg-black py-5 text-white hover:bg-zinc-800">
            Save
          </Button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="delete-category-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Delete this category?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{deleteTarget?.name}&rdquo; will be removed. Products in it become uncategorized &mdash; they are not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-category-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} data-testid="delete-category-confirm" className="rounded-full bg-red-600 text-white hover:bg-red-700">
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
